import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login, logout, ready, sessionState, retrySession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (sessionState === "unavailable") {
    return (
      <div
        className="min-h-[80vh] flex flex-col items-center justify-center gap-4 px-6 py-12 text-sm text-white/70"
        data-testid="admin-auth-unavailable"
        data-admin-auth-state="unavailable"
        role="status"
      >
        <p>We couldn’t check your sign-in. Try again.</p>
        <button type="button" className="btn-gold rounded px-5 py-2" onClick={retrySession} data-testid="admin-session-retry">
          Try again
        </button>
      </div>
    );
  }

  if (ready && user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }
  if (ready && user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  if (!ready) {
    return (
      <div
        className="min-h-[80vh] flex items-center justify-center px-6 py-12 text-sm text-white/55"
        data-testid="admin-auth-resolving"
        data-admin-auth-state="resolving"
        role="status"
      >
        Resolving admin session...
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const loggedInUser = await login(email, password);
      if (loggedInUser?.role !== "admin") {
        logout();
        toast.error("Admin access required.");
        return;
      }
      toast.success("Logged in.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-[80vh] flex items-center justify-center px-6 py-12"
      data-testid="admin-auth-required"
      data-admin-auth-state="required"
    >
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-lg border border-white/10 bg-white/[0.03] p-8 backdrop-blur"
      >
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[var(--cc-gold)] text-[var(--cc-navy)]">
            <ShieldCheck weight="fill" size={20} />
          </span>
          <div>
            <div className="overline text-white/40">CompChecker</div>
            <div className="font-display text-xl font-semibold">Admin sign in</div>
          </div>
        </div>
        <label className="overline text-white/50 block mb-2">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="admin-email-input"
          className="w-full bg-white/5 border border-white/15 rounded px-3 py-2.5 text-sm mb-4 outline-none focus:border-[var(--cc-gold)]"
          placeholder="Email address"
        />
        <label className="overline text-white/50 block mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          data-testid="admin-password-input"
          className="w-full bg-white/5 border border-white/15 rounded px-3 py-2.5 text-sm mb-6 outline-none focus:border-[var(--cc-gold)]"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="btn-gold w-full py-3 rounded text-sm disabled:opacity-60"
        >
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}
